import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { queryClient } from '~app/query-client';

import {
  getBulkChargeOutcome,
  useBulkChargeStudents,
} from '~entities/payment/api/bulk-charge-students';
import { useGetTransactionCode } from '~entities/payment/api/get-transaction-codes';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { type Student } from '~pages/payments/bulk-charge-students/index';

import { useGetCurrency } from '~shared/api/currency-api';
import { KEYS } from '~shared/api/query-keys';
import { DateTimePicker } from '~shared/components/date-time-picker';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { Switch } from '~shared/shadcn/ui/switch';
import {
  formatScheduledDate,
  getEarliestScheduledDay,
  parseScheduledDate,
} from '~shared/utils/scheduled-date';

interface ChargeDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (chargeName: string, amount: number) => void;
  students: Array<Student>;
}

export function ChargeDetailsDialog({
  open,
  onOpenChange,
  onComplete,
  students,
}: ChargeDetailsDialogProps) {
  const navigate = useNavigate();
  const [chargeName, setChargeName] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionCodeId, setTransactionCodeId] = useState<string>('');
  const [schedulePayment, setSchedulePayment] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<string | null>(null);
  const [skipPaymentMethod, setSkipPaymentMethod] = useState(false);
  const { data: currency, isLoading: isCurrencyLoading } = useGetCurrency();
  const { data: studioDetails } = useGetStudioDetails();
  const isV2 = studioDetails?.isV2 === true;

  const { data: transactionCodes } = useGetTransactionCode();

  const bulkChargeMutation = useBulkChargeStudents();

  const handleSubmit = () => {
    if (!chargeName || !amount || Number.parseFloat(amount) <= 0) {
      return;
    }

    // Validate that if scheduling is enabled, a date must be selected
    if (schedulePayment && !scheduledDate) {
      toast.error('Please select a scheduled date when scheduling is enabled.');
      return;
    }

    void bulkChargeMutation.mutateAsync(
      {
        studentId: students.map((s) => s._id),
        amount: Number.parseFloat(amount).toString(),
        chargeName,
        ...(transactionCodeId !== '' && { transactionCodeId }),
        schedulePayment,
        scheduledDate: schedulePayment ? scheduledDate : null,
        skipPaymentMethod,
        isV2,
      },
      {
        onSuccess: (data) => {
          const outcome = getBulkChargeOutcome(data);
          const firstFailureReason = data.results?.find(
            (result) => result.status === 'failed' && result.reason?.trim(),
          )?.reason;

          if (outcome.type === 'error') {
            toast.error(
              firstFailureReason
                ? `${outcome.message}: ${firstFailureReason}`
                : outcome.message,
            );
          } else if (outcome.type === 'partial') {
            toast.warning(
              firstFailureReason
                ? `${outcome.message}: ${firstFailureReason}`
                : outcome.message,
            );
          } else {
            toast.success(outcome.message);
          }

          onComplete(chargeName, Number.parseFloat(amount));
          void navigate({
            to: '/payment/class-charge-history',
          });
          setTimeout(() => {
            void queryClient.invalidateQueries({
              predicate(query) {
                return query.queryKey[0] === KEYS.BULK_CHARGE_HISTORY;
              },
            });
          }, 1000);
          setTimeout(() => {
            void queryClient.invalidateQueries({
              predicate(query) {
                return query.queryKey[0] === KEYS.BULK_CHARGE_HISTORY;
              },
            });
          }, 3000);
          setTimeout(() => {
            void queryClient.invalidateQueries({
              predicate(query) {
                return query.queryKey[0] === KEYS.BULK_CHARGE_HISTORY;
              },
            });
          }, 5000);
        },
        onError: () => {
          toast.error('Failed to charge students. Please try again.');
        },
      },
    );
  };

  const isSubmitting = bulkChargeMutation.isPending;

  // Memoize the earliest schedulable day to avoid recreating on every render
  const earliestScheduledDay = useMemo(() => getEarliestScheduledDay(), []);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) {
          setSkipPaymentMethod(false);
        }
      }}
    >
      <DialogContent className='sm:max-w-[425px]' aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Charge Details</DialogTitle>
        </DialogHeader>
        <div className='grid gap-4 py-4'>
          <div className='grid gap-2'>
            <Label htmlFor='charge-name' className='w-max'>
              Charge Name
            </Label>
            <Input
              id='charge-name'
              placeholder='Enter charge name'
              value={chargeName}
              onChange={(e) => {
                setChargeName(e.target.value);
              }}
              disabled={isSubmitting}
            />
          </div>
          <div className='grid gap-2'>
            <Label htmlFor='transaction-code' className='w-max'>
              Transaction Code
            </Label>
            <Select
              onValueChange={setTransactionCodeId}
              defaultValue={transactionCodeId}
            >
              <SelectTrigger className='' id='transaction-code'>
                <SelectValue placeholder='Select Transaction Code' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Select Transaction Code</SelectLabel>
                  {transactionCodes?.map((transactionCode) => (
                    <SelectItem
                      key={transactionCode._id}
                      value={transactionCode._id}
                    >
                      {transactionCode.code}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className='grid gap-2'>
            <Label htmlFor='amount' className='w-max'>
              Amount
            </Label>
            <div className='relative flex items-center'>
              <span className='absolute inset-y-0 left-3 flex h-full items-center'>
                {isCurrencyLoading ? '...' : (currency?.symbol ?? '$')}
              </span>
              <Input
                id='amount'
                type='number'
                min='1'
                placeholder='0'
                className='pl-7'
                value={amount}
                onChange={(e) => {
                  if (!/^\d*\.?\d{0,2}$/.test(e.target.value)) return;
                  setAmount(e.target.value);
                }}
                disabled={isSubmitting}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E' || e.key === '-') {
                    e.preventDefault();
                  }
                }}
              />
            </div>
          </div>

          <div className='grid gap-2'>
            <Label htmlFor='schedule-payment' className='w-max'>
              Schedule Payment
            </Label>
            <div className='flex flex-row items-center justify-between rounded-lg border p-4'>
              <div className='space-y-0.5'>
                <div className='text-base font-medium'>
                  Schedule the payment
                </div>
                <div className='text-sm text-muted-foreground'>
                  Enable to schedule this payment for a future date
                </div>
              </div>
              <Switch
                checked={schedulePayment}
                onCheckedChange={(checked) => {
                  setSchedulePayment(checked);
                  if (checked) {
                    setSkipPaymentMethod(false);
                  }
                }}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {schedulePayment && (
            <div className='grid gap-2'>
              <Label htmlFor='scheduled-date' className='w-max'>
                Scheduled Date
              </Label>
              <DateTimePicker
                hourCycle={12}
                granularity='day'
                displayFormat={{ hour12: 'PPP' }}
                value={
                  scheduledDate
                    ? (parseScheduledDate(scheduledDate) ?? undefined)
                    : undefined
                }
                onChange={(date) => {
                  setScheduledDate(date ? formatScheduledDate(date) : null);
                }}
                disabledDays={(date) => date < earliestScheduledDay}
              />
            </div>
          )}

          {!schedulePayment && (
            <div className='flex items-start space-x-2 rounded-lg border p-4'>
              <Checkbox
                className='mt-0.5'
                id='bulk-charge-skip-payment-method'
                checked={skipPaymentMethod}
                onCheckedChange={(checked) => {
                  setSkipPaymentMethod(checked === true);
                }}
                disabled={isSubmitting}
              />
              <div className='space-y-1'>
                <Label
                  htmlFor='bulk-charge-skip-payment-method'
                  className='text-sm font-medium'
                >
                  No immediate charge
                </Label>
                <p className='text-sm text-muted-foreground'>
                  Post the charge to the parent account without collecting
                  payment now. They can pay the outstanding balance later.
                </p>
              </div>
            </div>
          )}

          {skipPaymentMethod && (
            <p className='text-sm text-muted-foreground'>
              Charges will be posted as outstanding balances — no card will be
              charged at this time.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !chargeName ||
              !amount ||
              Number.parseFloat(amount) <= 0 ||
              (schedulePayment && !scheduledDate) ||
              isSubmitting
            }
          >
            {isSubmitting
              ? 'Processing...'
              : skipPaymentMethod
                ? 'Post Charge'
                : 'Charge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
